import { ClipboardList, FileText, ShieldCheck, Siren, MonitorCog, Users, Truck, GraduationCap, Radio, Route, Video, CircleCheckBig } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useReveal } from "../../hooks/useReveal";
import { Section, VCOLOR, VTEXT, VERTICAL_KEYS } from "./Primitives";

const VICONS = {
  agro: <path d="M24 4 L24 12 M18 8 L30 8 M12 20 h24 v20 a2 2 0 0 1 -2 2 h-20 a2 2 0 0 1 -2 -2 Z M12 28 h24 M20 20 v22 M28 20 v22" />,
  mines: <path d="M6 38 L18 14 L26 28 L32 18 L42 38 Z M4 40 h40 M14 40 v-6 M34 40 v-4" />,
  petrole: <path d="M14 44 V20 a10 10 0 0 1 20 0 v24 M14 30 h20 M24 4 c-4 4 -4 8 0 10 c4 -2 4 -6 0 -10 Z" />,
  numerique: <><rect x="16" y="16" width="16" height="16" rx="2" /><path d="M24 4 v10 M24 34 v10 M4 24 h10 M34 24 h10 M10 10 l6 6 M32 32 l6 6 M38 10 l-6 6 M16 32 l-6 6" /></>,
};

export const Verticals = () => {
  const { t } = useLang();
  const s = t.verticalsSection;
  return (
    <Section id="verticaux" eyebrow={s.eyebrow} title={s.title} desc={s.desc} testid="verticals-section">
      <div className="verticals stagger">
        {VERTICAL_KEYS.map((k) => {
          const c = s.cards[k];
          return (
            <article className="panel v-card panel-hover" key={k} style={{ "--c": VCOLOR[k], "--ct": VTEXT[k] }} data-testid={`vertical-card-${k}`}>
              <svg className="v-icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">{VICONS[k]}</svg>
              <h3>{t.verticals[k]}</h3>
              <p className="v-sub">{c.sub}</p>
              <ul>{c.items.map((it) => <li key={it}>{it}</li>)}</ul>
              <div className="tags">{c.tags.map((tg) => <span className="tag" key={tg}>{tg}</span>)}</div>
              <a href="#contact" className="s-badge" data-testid={`vertical-audit-link-${k}`}><ClipboardList aria-hidden="true" />{c.badge}</a>
            </article>
          );
        })}
      </div>
      <p className="footnote">{s.footnote}</p>
    </Section>
  );
};

const SICONS = [Siren, MonitorCog, Users, Truck, GraduationCap];

export const Services = () => {
  const { t } = useLang();
  const s = t.services;
  return (
    <Section id="services" eyebrow={s.eyebrow} title={s.title} desc={s.desc} testid="services-section">
      <div className="services stagger">
        {s.cards.map((c, i) => {
          const Icon = SICONS[i];
          return (
            <article className="panel s-card panel-hover" key={c.num} data-testid={`service-card-${i + 1}`}>
              <div className="s-top"><span className="s-num">{c.num}</span><span className="s-icon"><Icon aria-hidden="true" /></span></div>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
              <div className="mini-flow">{c.flow.map((f) => <div key={f}>{f}</div>)}</div>
              {c.badge && <a href={c.badge[1]} className="s-badge"><FileText aria-hidden="true" />{c.badge[0]}</a>}
              {i === 4 && <a href="#academie" className="s-badge" data-testid="service-academy-link"><GraduationCap aria-hidden="true" />{t.academy.catalogLink}</a>}
            </article>
          );
        })}
      </div>
    </Section>
  );
};

const PICONS = [Radio, Route, Video, CircleCheckBig];

export const Process = () => {
  const { t } = useLang();
  const ref = useReveal();
  return (
    <Section id="parcours" eyebrow={t.process.eyebrow} title={t.process.title} testid="process-section">
      <div className="panel process reveal" ref={ref}>
        {t.process.steps.map(([title, desc], i) => {
          const Icon = PICONS[i];
          return (
            <div className="p-step" key={title} data-testid={`process-step-${i + 1}`}>
              <span className="p-bar" style={{ transitionDelay: `${i * 0.25}s` }} aria-hidden="true" />
              <div className="p-head"><span className="p-n">0{i + 1}</span><span className="p-icon"><Icon aria-hidden="true" /></span></div>
              <h4>{title}</h4>
              <p>{desc}</p>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const GICONS = [
  <path d="M24 4 L42 11 V22 C42 33 34 41 24 44 C14 41 6 33 6 22 V11 Z M17 24 L22 29 L32 18" />,
  <path d="M12 4 h16 l8 8 v32 h-24 Z M28 4 v8 h8 M18 22 h12 M18 29 h12 M18 36 h8" />,
  <path d="M4 32 V14 h22 v18 Z M26 22 h10 l8 8 v2 h-18 Z M13 38 a4 4 0 1 0 0.01 0 Z M35 38 a4 4 0 1 0 0.01 0 Z" />,
];

export const Guarantees = () => {
  const { t } = useLang();
  const g = t.guarantees;
  return (
    <Section id="garanties" eyebrow={g.eyebrow} title={g.title} desc={g.desc} testid="guarantees-section">
      <div className="guarantees stagger">
        {g.cards.map((c, i) => (
          <article className="panel g-card panel-hover" key={c.title} data-testid={`guarantee-card-${i + 1}`}>
            <svg className="g-icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">{GICONS[i]}</svg>
            <h3>{c.title}</h3>
            <p>{c.desc}</p>
            {c.badges?.map((b) => <a key={b} href="#contact" className="s-badge"><ShieldCheck aria-hidden="true" />{b}</a>)}
          </article>
        ))}
      </div>
    </Section>
  );
};
